'use client'

/**
 * Gedeelde "sessie verlopen"-signalering voor de portal-shell (FO-374 C).
 *
 * Achtergrond: een open tab blijft na sessieverval de bell-badges pollen (SWR,
 * 30-60s). Elke poll gaf dan stil een 401 tot de tab sloot - ruis in het
 * foutprofiel, en de monteur merkte pas bij een klik dat hij uitgelogd was.
 *
 * Zodra een authed poll een **401** ziet, zet `markSessionExpired()` één keer
 * dit globale signaal. Consumers reageren:
 *   - SWR wordt globaal gepauzeerd (`isPaused` in de SWRConfig) -> geen verdere
 *     poll-fouten;
 *   - `<SessionExpiredNotice>` toont de herlogin-flow.
 *
 * **403 is bewust GEEN sessieverval.** Een 403 betekent "wel ingelogd, geen
 * recht op dit endpoint" - opnieuw inloggen lost dat nooit op. Toen 403 hier
 * wél meetelde, kwam een gebruiker zonder recht op een badge-endpoint na de
 * login direct op "Sessie verlopen - opnieuw inloggen" en daarmee in een
 * onontkoombare loop. Een 403 zet nu de per-key `markPollForbidden()`-latch:
 * die ene poll stopt (geen 403-ruis), de sessie blijft intact.
 *
 * Singleton op module-niveau (niet in React-state) zodat losse hooks/fetchers
 * zonder gedeelde provider hetzelfde signaal delen.
 */
import { useSyncExternalStore } from 'react'

let expired = false
const listeners = new Set<() => void>()

/** Markeer de sessie als verlopen. Idempotent; notificeert subscribers één keer. */
export function markSessionExpired(): void {
  if (expired) return
  expired = true
  for (const listener of listeners) listener()
}

/** Huidige waarde (voor niet-React consumers zoals SWR `isPaused`). */
export function isSessionExpired(): boolean {
  return expired
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** React-hook die her-rendert zodra de sessie verloopt. */
export function useSessionExpired(): boolean {
  return useSyncExternalStore(subscribe, isSessionExpired, () => false)
}

/**
 * True bij een verlopen/ongeldige sessie op een authed poll.
 * UITSLUITEND 401 - zie de 403-uitleg in de module-doc hierboven.
 */
export function isSessionExpiredStatus(status: number): boolean {
  return status === 401
}

// ── 403-latch per poll-key ──────────────────────────────────────────────────
// Een endpoint waar deze gebruiker geen recht op heeft, blijft dat de hele
// sessie. De key wordt daarom eenmalig "verboden" gemarkeerd; consumers zetten
// hun SWR-key op null (poll stopt, badge verdwijnt) zonder de sessie te raken.

const forbiddenKeys = new Set<string>()
const forbiddenListeners = new Set<() => void>()

/** Markeer één poll-key als verboden (403). Idempotent. */
export function markPollForbidden(key: string): void {
  if (forbiddenKeys.has(key)) return
  forbiddenKeys.add(key)
  for (const listener of forbiddenListeners) listener()
}

/** Is deze poll-key eerder op 403 gelopen? */
export function isPollForbidden(key: string): boolean {
  return forbiddenKeys.has(key)
}

function subscribeForbidden(listener: () => void): () => void {
  forbiddenListeners.add(listener)
  return () => {
    forbiddenListeners.delete(listener)
  }
}

/**
 * SWR-key die zichzelf uitschakelt zodra het endpoint 403 gaf.
 * Gebruik: `useSWR(usePollKey(KEY), fetcher, …)` - na een 403 wordt de key
 * `null` en stopt SWR met pollen (in plaats van elke minuut te blijven 403'en).
 */
export function usePollKey(key: string): string | null {
  const forbidden = useSyncExternalStore(
    subscribeForbidden,
    () => forbiddenKeys.has(key),
    () => false,
  )
  return forbidden ? null : key
}

/**
 * Alleen voor tests: reset de globale signalen tussen cases.
 * Niet gebruiken in productiecode.
 */
export function __resetSessionExpiredForTests(): void {
  expired = false
  listeners.clear()
  forbiddenKeys.clear()
  forbiddenListeners.clear()
}
